import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Result,
  Tabs,
  Form,
  Table,
  Row,
  Modal,
  Typography,
  message,
  Space,
} from 'antd';
import { FormLabel, ModalDialog } from 'components/common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import uiTypeRender from 'utils/uiTypeRender';
import { isJsonString } from 'utils';
import {
  fetchSettings,
  fetchWarehouseSettings,
  saveWarehouseSettings,
} from 'services/warehouse';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

type LOTSettingDialogProps = {
  warehouse: WarehouseRow;
  onClose: () => void;
  onRefresh: () => void;
  visible: boolean;
};

type RuleCondition = {
  type: number;
  code: string;
  op: number; // 0: =, 1: >, 2: <
  value: string | number;
};

type RuleGroup = {
  condition: RuleCondition[];
};

function validateRule(
  obj: { [key: string]: any },
  ruleGroups: RuleGroup[]
): boolean {
  if(ruleGroups.length === 0) return false;
  return !ruleGroups.every((group) => {
    return group.condition.some((condition) => {
      const objValue = obj[condition.code];
      switch (condition.op) {
        case 0: // =
          return objValue === condition.value;
        // case 1: // >
        //   return objValue > condition.value;
        // case 2: // <
        //   return objValue < condition.value;
        default:
          return false;
      }
    });
  });
}

function transformKeys(obj: { [key: string]: any }): { [key: string]: any } {
  const result: { [key: string]: any } = {};

  for (const [key, value] of Object.entries(obj)) {
    const parts = key.split('_$_$_');
    if (parts.length > 1) {
      result[parts[1]] = value;
    }
  }
  return result;
}

const renderValue = (thirdPartySettingDefUI: any, defaultValue: any) => {
  switch (thirdPartySettingDefUI.uiType) {
    case 1:
    case 2:
    case 8:
      return defaultValue;
    case 3:
      return moment(defaultValue).format('MM/DD/YYYY');
    case 4:
    case 5:
    case 6:
      if (isJsonString(thirdPartySettingDefUI.optionList)) {
        const list1 = JSON.parse(thirdPartySettingDefUI.optionList);
        const temp = list1.filter((i: any) => i.Value === defaultValue);
        if (temp.length > 0) {
          return temp[0].Name || defaultValue;
        } else {
          return defaultValue;
        }
      } else {
        return defaultValue;
      }

    case 7:
      if (isJsonString(thirdPartySettingDefUI.optionList)) {
        const list3 = JSON.parse(thirdPartySettingDefUI.optionList);

        const temp = list3.filter(
          (i: any) => defaultValue.indexOf(i.Value) > -1
        );
        if (temp.length > 0) {
          return temp.map((i: any) => i.Name) || defaultValue;
        } else {
          return defaultValue;
        }
      } else {
        return defaultValue;
      }
    case 11:
      return moment(defaultValue).format('h:mm:ss A');

    case 12:
      return '********';
    default:
      return defaultValue;
  }
};

const filterValue = (
  thirdPartySettingDefUI: any,
  defaultValue: any,
  newValue: any
) => {
  switch (thirdPartySettingDefUI.uiType) {
    case 1:
    case 2:
    case 8:
      return defaultValue === newValue;
    case 3:
      return (
        moment(defaultValue).format('MM/DD/YYYY') ===
        moment(newValue).format('MM/DD/YYYY')
      );
    case 4:
    case 5:
    case 6:
      return `${defaultValue}` === `${newValue}`;

    case 7:
      return `${defaultValue.toString()}` === `${newValue.toString()}`;
    case 11:
      return (
        moment(defaultValue).format('h:mm:ss A') ===
        moment(newValue).format('h:mm:ss A')
      );

    case 12:
      return !newValue;
    default:
      return true;
  }
};

const columns = [
  {
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
  },
  {
    title: 'Setting Name',
    dataIndex: 'settingName',
    key: 'settingName',
  },
  {
    title: 'Old Value',
    dataIndex: 'settingValue',
    key: 'settingValue',
    render: (text: any, record: any) => {
      const v = renderValue(record.profileSettingDefUI, text);
      return v;
    },
  },
  {
    title: 'New Value',
    dataIndex: 'newValue',
    key: 'newValue',
    render: (text: any, record: any) => {
      const v = renderValue(record.profileSettingDefUI, text);
      return v;
    },
  },
];

const WarehouseSettingDialog = (props: LOTSettingDialogProps) => {
  const { onClose, visible, warehouse } = props;
  const [detailDialogIsFullscreen, setDetailDialogIsFullscreen] =
    useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);
  const [settings, setSettings] = useState<any[]>([]);
  const [settingOptionList, setSettingOptionList] = useState<StringKAnyVPair>({});
  const [userSetting, setUserSetting] = useState<{ [key: string]: any }>({});
  const [activeTab, setActiveTab] = useState<string>();
  const [formChanged, setFormChanged] = useState(false);
  const [tempSettings, setTempSettings] = useState<any[]>([]);
  const [inited, setInited] = useState(false);
  const [conditions, setConditions] = useState<StringKAnyVPair>({});
  const [hides, setHides] = useState<StringKAnyVPair>({}); //control all item display

  const refreshBtnRef = useRef<any>(null);

  const [form] = Form.useForm();

  const detailDialogWidth = useCallback(
    (isFullscreen = detailDialogIsFullscreen) => {
      return isFullscreen
        ? window.innerWidth - 4
        : window.innerWidth > 1280
        ? window.innerWidth * 0.8
        : 1200;
    },
    [detailDialogIsFullscreen]
  );

  const getDialogDom = () => {
    const refreshBtn = refreshBtnRef?.current;

    if (refreshBtn) {
      return refreshBtn.parentNode.parentNode.parentNode.parentNode.parentNode
        .parentNode.parentNode;
    }

    return null;
  };

  const onFullscreen = (isFullscreen: boolean) => {
    const dialog = getDialogDom();

    setDetailDialogIsFullscreen(isFullscreen);

    if (dialog) {
      const width = detailDialogWidth(isFullscreen);

      dialog.style.width = `${width}px`;
    }
  };

  const checkConditions = useCallback((tempValues, hds, cdts) => {
    const tempHides: StringKAnyVPair = { ...hds };
    Object.keys(hds).map((i) => {
      if (cdts[i]) {
        tempHides[i] = validateRule(tempValues, cdts[i]);
      }
      return true;
    });
    // console.log('tempHides', tempHides)
    setHides(tempHides);
  }, []);

  const getSettings = useCallback(async () => {
    const temp: { [key: string]: any } = {};
    const olDict: StringKAnyVPair = {};
    try {
      setLoading1(true);
      const res = await fetchWarehouseSettings(warehouse.warehouseNum || 0);
      setLoading1(false);
      //console.log('res of settings', res);
      if (res.isSuccess) {
        res.data.map((item: any) => {
          temp[item.settingCode] = item.settingValue;

          if (typeof item.optionList === 'string') {
            if (item.settingCode === 'Inventory.ImposedAdjustmentLocation') {
              try {
                const ol = JSON.parse(item.optionList);

                //if (Array.isArray(ol)) olDict[item.settingCode] = ol;
                if (Array.isArray(ol)) olDict[item.settingCode] = item.optionList;
              } catch(e) {}
            }
          }

          return true;
        });
        //console.log(olDict);
        setUserSetting({ ...temp, isReady: true });
        setSettingOptionList(olDict);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading1(false);
    }
    return temp;
  }, [warehouse.warehouseNum]);

  const getSettingDefs = useCallback(async () => {
    try {
      setLoading(true);
      const tempValues = await getSettings();
      //console.log('tempValues1', tempValues)
      const res = await fetchSettings();
      setLoading(false);
      if (res.isSuccess) {
        setSettings(res.data);
        if (!activeTab && res.data.length > 0) {
          setActiveTab(res.data[0].category);
        }
        const temp: any = [];
        const tempConditions: StringKAnyVPair = {};
        const tempHides: StringKAnyVPair = {};
        res.data.map((i: any) => {
          i.scopes.map((k: any) => {
            k.scopeItems.map((j: any) => {
              temp.push({
                ...j,
                path: `${i.category} -> ${k.scope}`,
                canBeModifiedByCustomer: 0,
              });
              tempConditions[`${i.category}_$_$_${j.settingCode}`] =
                j.conditions;
              tempHides[`${i.category}_$_$_${j.settingCode}`] = false;
              return true;
            });
            return true;
          });
          return true;
        });
        setTempSettings([...temp]);
        setConditions(tempConditions);
        //console.log('-->', temp, tempConditions);
        checkConditions(tempValues, tempHides, tempConditions);
      }
    } catch (error) {
      setLoading(false);
    }
  }, [activeTab, getSettings, checkConditions]);

  const handleSave = async () => {
    try {
      const params = await form.validateFields();
      //console.log('p', params); return;
      if (params.errorFields) return;
      const postParams: any = [];

      const changedCode: string[] = [];
      const changedCodeKeyValue: { [key: string]: any } = {};
      for (let i in params) {
        const tmp = i.split('_$_$_');
        if (tmp.length === 2) {
          postParams.push({
            SettingCode: tmp[1],
            SettingValue: params[i],
          });
          if (params[i] !== userSetting[tmp[1]]) {
            changedCode.push(tmp[1]);
            changedCodeKeyValue[tmp[1]] = params[i];
          }
        }
      }
      if (changedCode.length > 0 || formChanged) {
        const json = JSON.stringify(postParams);
        
        try {
          setSaveLoading(true);
          const res = await saveWarehouseSettings(
            warehouse.warehouseNum || 0,
            json
          );
          setSaveLoading(false);
          if (res.isSuccess) {
            message.success({ content: 'Saved Successfully' });
            // getUserSettings();
            getSettings();
            setFormChanged(false);
          }
        } catch (error) {
          setSaveLoading(false);
        }
      }
    } catch (error: any) {
      if (
        error &&
        error.errorFields &&
        error.errorFields.length > 0 &&
        error.errorFields[0].name &&
        error.errorFields[0].name.length > 0
      ) {
        const t = error.errorFields[0].name[0].split('_$_$_');
        if (t.length === 2) {
          setActiveTab(t[0]);
        }
        if (
          error &&
          error.errorFields &&
          error.errorFields.length > 0 &&
          error.errorFields[0].errors &&
          error.errorFields[0].errors.length > 0
        )
          message.info(error.errorFields[0].errors[0]);
      }
      setSaveLoading(false);
    }
  };

  // eslint-disable-next-line
  const handleSave1 = useCallback(async () => {
    try {
      const params = await form.validateFields();
      //console.log('p', params); return;
      if (params.errorFields) return;
      const postParams: any = [];

      const changedCode: string[] = [];
      const changedCodeKeyValue: { [key: string]: any } = {};
      for (let i in params) {
        const tmp = i.split('_$_$_');
        if (tmp.length === 2) {
          postParams.push({
            SettingCode: tmp[1],
            SettingValue: params[i],
          });
          if (params[i] !== userSetting[tmp[1]]) {
            changedCode.push(tmp[1]);
            changedCodeKeyValue[tmp[1]] = params[i];
          }
        }
      }
      if (changedCode.length > 0 || formChanged) {
        const temp = tempSettings
          .filter((k: any) => changedCode.indexOf(k.settingCode) > -1)
          .map((i: any) => {
            return {
              ...i,
              settingValue: userSetting[i.settingCode],
              newValue: changedCodeKeyValue[i.settingCode],
            };
          });
        const filterTemp = temp.filter(
          (i) => !filterValue(i.profileSettingDefUI, i.settingValue, i.newValue)
        );
        const json = JSON.stringify(postParams);
        Modal.confirm({
          width: window.innerWidth > 1200 ? window.innerWidth * 0.8 : 800,
          bodyStyle: {
            width: window.innerWidth > 1200 ? window.innerWidth * 0.8 : 800,
          },
          title: 'These settings will be updated',
          content: (
            <Table
              size="small"
              scroll={{ y: 500 }}
              dataSource={filterTemp}
              columns={columns}
              pagination={false}
            />
          ),
          onOk: async () => {
            try {
              setSaveLoading(true);
              const res = await saveWarehouseSettings(
                warehouse.warehouseNum || 0,
                json
              );
              setSaveLoading(false);
              if (res.isSuccess) {
                message.success({ content: 'Saved Successfully' });
                // getUserSettings();
                getSettings();
                setFormChanged(false);
              }
            } catch (error) {
              setSaveLoading(false);
            }
          },
        });
      }
    } catch (error: any) {
      if (
        error &&
        error.errorFields &&
        error.errorFields.length > 0 &&
        error.errorFields[0].name &&
        error.errorFields[0].name.length > 0
      ) {
        const t = error.errorFields[0].name[0].split('_$_$_');
        if (t.length === 2) {
          setActiveTab(t[0]);
        }
        if (
          error &&
          error.errorFields &&
          error.errorFields.length > 0 &&
          error.errorFields[0].errors &&
          error.errorFields[0].errors.length > 0
        )
          message.info(error.errorFields[0].errors[0]);
      }
      setSaveLoading(false);
    }
  }, [form, formChanged, getSettings, tempSettings, userSetting, warehouse.warehouseNum]);

  const onFormItemChange = (key: string, val: any, settingCode?: string) => {
    const fv = form.getFieldsValue();

    setFormChanged(fv[key] !== val);
    fv[key] = val;
    //console.log('ch', key, val, fv);
    form.setFieldsValue(fv);

    if (settingCode) {
      userSetting[settingCode] = val;
      setUserSetting({...userSetting});
    }
  };

  useEffect(() => {
    if (!inited) {
      getSettingDefs();
      setInited(true);
    }
  }, [getSettingDefs, inited]);

  return (
    <ModalDialog
      centered
      className="fullscreen-modal"
      closable={false}
      closeButton={true}
      // footer={null}
      fullscreen={true}
      maskClosable={false}
      onClose={onClose}
      onFullscreen={onFullscreen}
      title={`Warehouse Settings - ${warehouse.warehouseCode}`}
      visible={visible}
      width={detailDialogWidth()}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose} icon={<CloseOutlined />}>
              Cancel
            </Button>
            <Button
              disabled={!formChanged}
              type="primary"
              loading={saveLoading}
              onClick={() => handleSave()}
              icon={<SaveOutlined />}
            >
              Save
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        form={form}
        onValuesChange={(changedValues, values) => {
          checkConditions(transformKeys(values), hides, conditions);
          setFormChanged(true);
        }}
        style={{ height: '100%' }}
        labelCol={{
          style: { width: 350, display: 'flex', justifyContent: 'start' },
        }}
        wrapperCol={{
          style: { width: 'calc(100% - 300px)' },
        }}
      >
        <Card
          style={{ width: '100%', height: '100%' }}
          loading={loading || loading1}
          bodyStyle={{ padding: 8 }}
        >
          {settings.length > 0 ? (
            userSetting &&
            userSetting.isReady &&
            activeTab && (
              <Tabs
                activeKey={activeTab}
                size="middle"
                type="card"
                onChange={(value) => setActiveTab(value)}
              >
                {settings.map((i: any) => (
                  <TabPane
                    tab={i.category}
                    key={i.category}
                    style={{ overflowY: 'auto' }}
                  >
                    {i.scopes.map((m: any, idx2: number) => (
                      <Card
                        key={`${m.scope}${idx2}`}
                        size="small"
                        bordered={false}
                        style={{ marginBottom: 10 }}
                        title={<Title level={5}>{m.scope}</Title>}
                      >
                        {m.scopeItems.map((f: any, index: number) => {
                          if (settingOptionList[f.settingCode]) {
                            //console.log('f->', f);
                            f.profileSettingDefUI.optionList = settingOptionList[f.settingCode];
                          }
                          return !f.isVisibleByCustomer ? null : (
                            <div key={`${f.settingCode}${index}`}>
                              <Form.Item
                                hidden={
                                  hides[`${i.category}_$_$_${f.settingCode}`]
                                    ? hides[
                                        `${i.category}_$_$_${f.settingCode}`
                                      ]
                                    : false
                                }
                                name={`${i.category}_$_$_${f.settingCode}`}
                                initialValue={
                                  f.profileSettingDefUI.uiType === 3 ||
                                  f.profileSettingDefUI.uiType === 11
                                    ? moment(userSetting[f.settingCode])
                                    : f.profileSettingDefUI.uiType === 7
                                    ? isJsonString(userSetting[f.settingCode])
                                      ? JSON.parse(userSetting[f.settingCode])
                                      : []
                                    : f.profileSettingDefUI.uiType === 12
                                    ? undefined
                                    : userSetting[f.settingCode]
                                }
                                label={
                                  <FormLabel noColon>{f.settingName}</FormLabel>
                                }
                                rules={[
                                  {
                                    required:
                                      f.profileSettingDefUI.uiType === 12 &&
                                      userSetting[f.settingCode]
                                        ? false
                                        : true,
                                    message: `${f.settingName} is required`,
                                  },
                                ]}
                              >
                                {uiTypeRender(
                                  f.profileSettingDefUI,
                                  !f.canBeModifiedByCustomer,
                                  {
                                    initialValue: userSetting[f.settingCode],
                                    onChange(tpl: string, val: any) {
                                      const itemValue = {
                                        Value: tpl,
                                        Parameters: [
                                          { Name: 'Header', Value: val.Header || '' },
                                          { Name: 'Barcode', Value: val.Barcode || '' },
                                          { Name: 'Footer', Value: val.Footer || '' },
                                          { Name: 'Logo', Value: val.Logo || '' },
                                        ],
                                      };
                                      onFormItemChange(`${i.category}_$_$_${f.settingCode}`, JSON.stringify(itemValue), f.settingCode);
                                    },
                                  },
                                )}
                              </Form.Item>
                              {f.note && (
                                <Text
                                  style={{ marginLeft: 20 }}
                                  type="secondary"
                                >
                                  {f.note}
                                </Text>
                              )}
                            </div>
                          );
                        })}
                      </Card>
                    ))}
                  </TabPane>
                ))}
              </Tabs>
            )
          ) : (
            <Result
              status="error"
              title="Failed to extract configuration data."
              subTitle="Please contact the system administrator."
            ></Result>
          )}
        </Card>
      </Form>
    </ModalDialog>
  );
};

export default WarehouseSettingDialog;
